import React, { ComponentProps, FC } from 'react'

import { useFragment } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Link } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { AttendanceEditTimesheetRequestApprovableFragmentDoc } from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { durationToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import NoteField from 'modules/inbox_tasks/components/NoteField'
import {
  calculateDuration,
  getTimeWithoutTimezone,
  locationTypeText,
} from 'modules/inbox_tasks/tasks/approval/AttendanceEditTimesheetRequest/helpers'
import { AttendanceEditTimesheetRequestTaskContentTypeProps } from 'modules/inbox_tasks/tasks/approval/AttendanceEditTimesheetRequest/types'

const UpdateShiftRequestContent: FC<AttendanceEditTimesheetRequestTaskContentTypeProps> = ({
  id,
  requesterContent,
  moreDetails,
  workedTime,
  affecteeName,
}) => {
  const { data } = useFragment({
    fragment: AttendanceEditTimesheetRequestApprovableFragmentDoc,
    fragmentName: 'AttendanceEditTimesheetRequestApprovable',
    from: {
      __typename: 'AttendanceEditTimesheetRequest',
      id,
    },
    optimistic: false,
  })

  if (!data) {
    return null
  }

  const date = data.attendanceShiftDate
    ? stringToDate(data.attendanceShiftDate, 'backend_datetime')
    : undefined
  const displayDate = date ? i18nDateToString(date, 'ui_dmy_long') : ''

  const originalShift = data.attendanceShift
  const originalClockIn = originalShift?.clockIn
  const originalClockOut = originalShift?.clockOut
  const originalLocationType = originalShift?.locationType
  const originalObservations = originalShift?.observations
  const originalIsBreak = originalShift?.workable === false
  const originalBreakName = originalShift?.timeSettingsBreakConfiguration?.name
  const originalBreakPaid = originalShift?.timeSettingsBreakConfiguration?.paid

  const newClockIn = data.clockIn
  const newClockOut = data.clockOut
  const newLocationType = data.locationType
  const newObservations = data.observations
  const newIsBreak = !data.workable
  const newBreakName = data.timeSettingsBreakConfiguration?.name
  const newBreakPaid = data.timeSettingsBreakConfiguration?.paid

  const originalDuration =
    originalClockIn && originalClockOut
      ? calculateDuration(originalClockIn, originalClockOut)
      : { hours: 0, minutes: 0 }

  const newDuration =
    (newClockIn || originalClockIn) && (newClockOut || originalClockOut)
      ? calculateDuration(newClockIn || originalClockIn, newClockOut || originalClockOut)
      : { hours: 0, minutes: 0 }

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    requesterContent,
    {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.date'),
      content: {
        text: displayDate,
        type: 'item' as const,
      },
    },
    {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.request_type'),
      content: {
        text: i18n.t(
          'tasks.approvals.attendance_edit_timesheet_request.fields.request_type_update'
        ),
        type: 'item' as const,
      },
    },
  ].filter(Boolean)

  const originalClockInTime = originalClockIn ? getTimeWithoutTimezone(originalClockIn) : undefined
  const originalClockOutTime = originalClockOut
    ? getTimeWithoutTimezone(originalClockOut)
    : undefined

  // Add clock in/out details for changes
  if (originalClockInTime && newClockIn && originalClockInTime !== newClockIn) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.clock_in'),
      content: {
        text: `${originalClockInTime} → ${newClockIn}`,
        type: 'item' as const,
      },
    })
  }

  if (originalClockOutTime && newClockOut && originalClockOutTime !== newClockOut) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.clock_out'),
      content: {
        text: `${originalClockOutTime} → ${newClockOut}`,
        type: 'item' as const,
      },
    })
  }

  if (originalLocationType && newLocationType && originalLocationType !== newLocationType) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.location_type'),
      content: {
        text: `${locationTypeText(originalLocationType)} → ${locationTypeText(newLocationType)}`,
        type: 'item' as const,
      },
    })
  }

  if (originalObservations && newObservations && originalObservations !== newObservations) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.observations'),
      content: {
        text: `${originalObservations} → ${newObservations}`,
        type: 'item' as const,
      },
    })
  }

  // Add duration change
  if (
    originalDuration.hours !== newDuration.hours ||
    originalDuration.minutes !== newDuration.minutes
  ) {
    const originalDurationText = durationToText(
      originalDuration.hours * 60 + originalDuration.minutes,
      'minutes'
    )
    const newDurationText = durationToText(newDuration.hours * 60 + newDuration.minutes, 'minutes')

    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.duration'),
      content: {
        text: `${originalDurationText} → ${newDurationText}`,
        type: 'item' as const,
      },
    })
  }

  // Add break information if it changed
  if (originalIsBreak !== newIsBreak || originalBreakName !== newBreakName) {
    const originalBreakText = originalIsBreak
      ? originalBreakName ||
        i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.break')
      : i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.work')
    const newBreakText = newIsBreak
      ? newBreakName || i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.break')
      : i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.work')

    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.activity_type'),
      content: {
        text: `${originalBreakText} → ${newBreakText}`,
        type: 'item' as const,
      },
    })
  }

  if (originalBreakPaid !== newBreakPaid) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.paid_break'),
      content: {
        text: `${originalBreakPaid ? i18n.t('common.yes') : i18n.t('common.no')} → ${
          newBreakPaid ? i18n.t('common.yes') : i18n.t('common.no')
        }`,
        type: 'item' as const,
      },
    })
  }

  const summary = `${
    date && affecteeName
      ? i18n.t('dashboard.todo.approvals.attendance.duration_change', {
          employee: affecteeName,
          date: i18nDateToString(date, 'ui_abbreviated_month_year'),
          original_duration_hours: originalDuration.hours,
          original_duration_minutes: originalDuration.minutes,
          new_duration_hours: newDuration.hours,
          new_duration_minutes: newDuration.minutes,
        })
      : ''
  }${
    workedTime
      ? '\n' + i18n.t('dashboard.todo.approvals.attendance.worked_time', { workedTime }) + '.'
      : ''
  }`

  return (
    <Box width="full" gap="s16">
      <DetailsItemsList details={details} tableView />
      <Box paddingBottom="s4">
        <F0Link href={moreDetails.url}>{moreDetails.title}</F0Link>
      </Box>
      {!!data.reason && (
        <NoteField
          label={i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.reason')}
          content={data.reason}
        />
      )}
      {!!summary && <NoteField content={summary} />}
    </Box>
  )
}

export default UpdateShiftRequestContent
